/**
 * Header nav — behavior-map rows 1 + 2 (block-map §3).
 *
 *   row 1  hamburger button `.cleo-header__toggle` (≤900px)
 *          → toggle the dropdown nav `.cleo-header__nav` (`is-open` +
 *            `aria-expanded`)
 *   row 2  mobile nav link click → close the dropdown
 *
 * Row 3 (real `<a href>` navigation + server-side active pill) and row 5
 * (`cleoFade` entrance) are markup/CSS only — ui-spec §7 says CSS-only, so
 * there is deliberately NO IntersectionObserver and no class juggling here.
 *
 * Vanilla JS, no jQuery, no framework. Loaded as an ES module by
 * src/js/child.js so Vite bundles it into assets/dist/js/child-theme.js.
 */

const TOGGLE_SELECTOR = ".cleo-header__toggle";
const NAV_SELECTOR = ".cleo-header__nav";

/**
 * Must stay in sync with `$bp-nav` / `tier3` in src/scss/_tokens.scss
 * (ui-spec §3 tier 3 — "hide inline nav, show hamburger").
 */
const NAV_BREAKPOINT = 900;

export function initHeaderNav() {
	const toggle = document.querySelector(TOGGLE_SELECTOR);
	const nav = document.querySelector(NAV_SELECTOR);

	if (!toggle || !nav) {
		return;
	}

	const isOpen = () => nav.classList.contains("is-open");

	const setOpen = (open) => {
		if (isOpen() === open) {
			return;
		}
		nav.classList.toggle("is-open", open);
		toggle.classList.toggle("is-open", open);
		toggle.setAttribute("aria-expanded", open ? "true" : "false");
	};

	// Row 1 — the hamburger toggles the dropdown.
	toggle.addEventListener("click", (event) => {
		event.preventDefault();
		setOpen(!isOpen());
	});

	// Row 2 — any link click inside the nav closes it. Delegated so links
	// injected later (e.g. by a menu block swap) still work, and so a click on
	// a nested <span> resolves to its anchor.
	nav.addEventListener("click", (event) => {
		if (event.target.closest("a")) {
			setOpen(false);
		}
	});

	// Escape closes and returns focus to the trigger (WCAG 2.1.2).
	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && isOpen()) {
			setOpen(false);
			toggle.focus();
		}
	});

	// A click anywhere outside the header closes it — the dropdown has no
	// backdrop of its own.
	document.addEventListener("click", (event) => {
		if (!isOpen()) {
			return;
		}
		if (event.target.closest(".cleo-header")) {
			return;
		}
		setOpen(false);
	});

	// Above the breakpoint the nav is inline and the toggle is display:none.
	// Drop a stale `is-open` (and its `aria-expanded="true"`) so the state can
	// never leak into the desktop layout after a rotate/resize.
	const sync = () => {
		if (window.innerWidth > NAV_BREAKPOINT) {
			setOpen(false);
		}
	};

	window.addEventListener("resize", sync, { passive: true });
	sync();
}
